import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { formatIso8601Duration, getWatchProgressByVideoId, type Video } from '@kidstube/shared';
import { useAppStore } from '../store/appStore';
import { applyFallbackThumbnail } from '../utils/thumbnailFallback';
import './VideoCard.css';

type WatchState = 'new' | 'continue' | 'watched';

function getWatchState(videoId: string, progressByVideoId: ReturnType<typeof getWatchProgressByVideoId>): WatchState {
    const progress = progressByVideoId.get(videoId);

    if (progress?.completed) return 'watched';
    if ((progress?.watchedDurationSeconds || 0) > 5) return 'continue';
    return 'new';
}

function getWatchStateLabel(state: WatchState): string {
    if (state === 'watched') return 'Watched';
    if (state === 'continue') return 'In-progress';
    return 'New';
}

export function VideoCard({ video, compact = false }: { video: Video; compact?: boolean }) {
    const thumb = video.snippet.thumbnails.high || video.snippet.thumbnails.medium || video.snippet.thumbnails.default;
    const navigate = useNavigate();
    const location = useLocation();
    const watchHistory = useAppStore(state => state.watchHistory);
    const progressByVideoId = useMemo(() => getWatchProgressByVideoId(watchHistory), [watchHistory]);
    const watchState = getWatchState(video.id, progressByVideoId);
    const progress = progressByVideoId.get(video.id);
    const durationLabel = formatIso8601Duration(video.contentDetails?.duration);

    const handleClick = (e: React.MouseEvent) => {
        e.preventDefault();
        // Navigate with state including channel info and fullscreen flag
        navigate(`/watch/${video.id}`, {
            state: {
                requestFullscreen: true,
                video,
                channel: {
                    id: video.snippet.channelId,
                    title: video.snippet.channelTitle
                },
                returnTo: `${location.pathname}${location.search}`
            }
        });
    };

    return (
        <a
            href={`/watch/${video.id}`}
            onClick={handleClick}
            className={`video-card ${compact ? 'video-card--compact' : ''}`}
            data-video-id={video.id}
            data-watch-state={watchState}
            aria-label={`${getWatchStateLabel(watchState)} video: ${video.snippet.title}`}
        >
            <div className="video-card__thumbnail">
                <img
                    src={thumb?.url || ''}
                    alt={video.snippet.title}
                    loading="lazy"
                    onError={event => applyFallbackThumbnail(event.currentTarget)}
                />
                <div className="video-card__play-icon">▶</div>
                {durationLabel && (
                    <span className="video-card__duration">
                        {durationLabel}
                    </span>
                )}
                {progress && progress.ratio > 0 && !progress.completed && (
                    <span className="video-card__progress" style={{ width: `${Math.round(progress.ratio * 100)}%` }} />
                )}
            </div>
            <div className="video-card__info">
                <h3 className="video-card__title">{video.snippet.title}</h3>
                <p className="video-card__channel">{video.snippet.channelTitle}</p>
            </div>
        </a>
    );
}
